

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1/admin`;

const QuestionList = () => {
    const [questions, setQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [questionText, setQuestionText] = useState('');

    // --- Pagination State ---
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [limit, setLimit] = useState(10); // Or whatever your default items per page is
     const [totalItems, setTotalItems] = useState(0);

    const fetchQuestions = useCallback(async (page = 1, pageSize = limit) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/questions?page=${page}&limit=${pageSize}`);
            if (response.data && response.data.questions) {
                setQuestions(response.data.questions);
                setTotalPages(response.data.totalPages || 0);
                 setTotalItems(response.data.totalItems || 0); // Store totalItems
                setCurrentPage(parseInt(response.data.page) || 1); // Ensure currentPage is updated from response
            } else {
                setQuestions([]);
                setTotalPages(0);
                  setTotalItems(0); // Reset totalItems
                setError('Failed to fetch questions: Invalid response format.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch questions.');
            console.error("Fetch error:", err);
            setQuestions([]); // Clear questions on error
            setTotalPages(0); // Reset total pages on error
        } finally {
            setIsLoading(false);
        }
    }, [limit]); // limit is a dependency if it can change

    useEffect(() => {
        fetchQuestions(currentPage, limit);
    }, [fetchQuestions, currentPage, limit]); // Refetch when currentPage or limit changes

    const handleOpenModalForAdd = () => {
        setCurrentQuestion(null);
        setQuestionText('');
        setError(null);
        setIsModalOpen(true);
    };

    const handleOpenModalForEdit = (question) => {
        setCurrentQuestion(question);
        setQuestionText(question.question);
        setError(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentQuestion(null);
        setQuestionText('');
        setError(null);
    };

    const handleSaveQuestion = async (e) => {
        e.preventDefault();
        if (!questionText.trim()) {
            setError("Question text cannot be empty.");
            return;
        }
        setIsLoading(true);
        setError(null);

        try {
            if (currentQuestion) {
                await axios.put(`${API_BASE_URL}/questions/${currentQuestion._id}`, { "question": questionText });
                toast.success("Question updated successfully");
            } else {
                await axios.post(`${API_BASE_URL}/questions`, { question: questionText });
                toast.success("Question added successfully");
            }
            // Fetch the current page again to see changes, especially if a new item might shift pagination
            fetchQuestions(currentPage, limit);
            handleCloseModal();
        } catch (err) {
            setError(err.response?.data?.message || (currentQuestion ? 'Failed to update question.' : 'Failed to add question.'));
            console.error("Save error:", err);
        } finally {
            setIsLoading(false);
        }
    };

const handleDeleteQuestion = async (id) => {
    const result = await Swal.fire({
        title: 'Are you sure you want to delete this question?',
        text: "This action cannot be undone.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
        customClass: {
            confirmButton: 'swal2-confirm-button',
            cancelButton: 'swal2-cancel-button'
        }
    });

    if (result.isConfirmed) {
        setIsLoading(true);
        setError(null);

        try {
            await axios.delete(`${API_BASE_URL}/questions/${id}`);
            toast.success("Question deleted successfully");

            if (questions.length === 1 && currentPage > 1) {
                setCurrentPage(prevPage => prevPage - 1);
            } else {
                fetchQuestions(currentPage, limit);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete question.');
            console.error("Delete error:", err);
            toast.error('Failed to delete question.');
        } finally {
            setIsLoading(false);
        }
    }
};


// const handleDeleteQuestion = (id) => {

//     const confirmDelete = () => {
//         setIsLoading(true);
//         setError(null);

//         axios.delete(`${API_BASE_URL}/questions/${id}`)
//             .then(() => {
//                 toast.success("Question deleted successfully");
//                 if (questions.length === 1 && currentPage > 1) {
//                     setCurrentPage(prevPage => prevPage - 1);
//                 } else {
//                     fetchQuestions(currentPage, limit);
//                 }
//             })
//             .catch((err) => {
//                 setError(err.response?.data?.message || 'Failed to delete question.');
//                 console.error("Delete error:", err);
//             })
//             .finally(() => {
//                 setIsLoading(false);
//             });
//     };

//     // Show confirmation toast
//     toast(
//         ({ closeToast }) => (
//             <div>
//                 <p>Are you sure you want to delete this question?</p>
//                 <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
//                     <button onClick={() => { confirmDelete(); closeToast(); }} style={{ background: 'red', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '4px' }}>
//                         Delete
//                     </button>
//                     <button onClick={closeToast} style={{ border: '1px solid gray', padding: '6px 10px', borderRadius: '4px' }}>
//                         Cancel
//                     </button>
//                 </div>
//             </div>
//         ),
//         { autoClose: false }
//     );
// };


    // --- Pagination Handlers ---
    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(prevPage => prevPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prevPage => prevPage - 1);
        }
    };

    const handleGoToPage = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    // --- Generate Page Numbers for Pagination UI ---
    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxPagesToShow = 10; // Max page numbers to show around current page
        const halfMaxPages = Math.floor(maxPagesToShow / 2);

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            let startPage = currentPage - halfMaxPages;
            let endPage = currentPage + halfMaxPages;

            if (startPage <= 0) {
                endPage -= (startPage - 1);
                startPage = 1;
            }
            if (endPage > totalPages) {
                startPage -= (endPage - totalPages);
                endPage = totalPages;
            }
             // Ensure startPage is at least 1 after adjustments
            startPage = Math.max(startPage, 1);

            if (startPage > 1) {
                pageNumbers.push(1);
                if (startPage > 2) {
                    pageNumbers.push('...');
                }
            }

            for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(i);
            }

            if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                    pageNumbers.push('...');
                }
                pageNumbers.push(totalPages);
            }
        }
        return pageNumbers;
    };


    return (
        <div className="p-6 bg-gray-50"> {/* Changed min-h-full to min-h-screen for better layout */}
            <div className="flex justify-between items-center gap-2 mb-6">
                <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 ">
                    Manage Questions
                </h2>
                <button
                    onClick={handleOpenModalForAdd}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                    Add New Question
                </button>
            </div>

            {isLoading && !isModalOpen && <p className="text-center text-gray-600 py-4">Loading questions...</p>}
            {!isLoading && error && !isModalOpen && <p className="text-center text-red-500 bg-red-100 p-3 rounded-md">{error}</p>}

            {!isLoading && !error && questions.length === 0 && totalPages === 0 && (
                <p className="text-center text-gray-500 mt-10">No questions found. Add some!</p>
            )}
             {!isLoading && !error && questions.length === 0 && totalPages > 0 && (
                <p className="text-center text-gray-500 mt-10">No questions on this page.</p>
            )}


            {!isLoading && questions.length > 0 && (
                <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead clas
                        Name="bg-gray-100">
                            <tr>
                                 <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-1/12"> {/* Changed w-10/12 to w-1/12 */}
                                    Sr No.
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-10/12">
                                    Question Text
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-2/12">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {questions.map((question,index) => (
                                <tr key={question._id} className="hover:bg-gray-50 transition-colors"> {/* Use _id if that's the unique key */}
                               <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">      {((currentPage - 1) * limit) + index + 1}</td>
                                    <td className="px-6 py-4 whitespace-pre-wrap break-words text-sm text-gray-700">
                                        {question.question}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <button
                                            onClick={() => handleOpenModalForEdit(question)}
                                            className="text-indigo-600 hover:text-indigo-900 mr-3 transition-colors"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDeleteQuestion(question._id)}
                                            className="text-red-600 hover:text-red-900 transition-colors"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* --- Pagination Controls --- */}
            {totalPages > 0 && !isLoading && (
              
                             <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
                    <div className="text-sm text-gray-700 mb-2 sm:mb-0">
                        Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span>
                        {totalItems > 0 && ( // Display total items if available
                            <span className="ml-2">(Total: <span className="font-medium">{totalItems}</span> records)</span>
                        )}
                    </div>
                    <nav className="inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1 || isLoading}
                            className={`relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            Previous
                        </button>
                        {getPageNumbers().map((page, index) => (
                            typeof page === 'number' ? (
                                <button
                                    key={index}
                                    onClick={() => handleGoToPage(page)}
                                    disabled={isLoading}
                                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium
                                        ${currentPage === page ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                                >
                                    {page}
                                </button>
                            ) : (
                                <span key={index} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                    {page} {/* ... */}
                                </span>
                            )
                        ))}
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages || isLoading}
                            className={`relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            Next
                        </button>
                    </nav>
                </div>
            )}


            {/* Modal for Add/Edit Question */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
                    <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-lg mx-auto">
                        <h2 className="text-2xl font-semibold mb-6 text-gray-800">
                            {currentQuestion ? 'Edit Question' : 'Add New Question'}
                        </h2>
                        <form onSubmit={handleSaveQuestion}>
                            <textarea
                                value={questionText}
                                onChange={(e) => setQuestionText(e.target.value)}
                                placeholder="Enter question text here..."
                                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 mb-4 h-32 resize-y"
                                rows="4"
                            />
                            {error && isModalOpen && <p className="text-red-500 text-sm mb-3">{error}</p>} {/* Show modal-specific errors */}
                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 transition-colors"
                                >
                                    {isLoading ? (currentQuestion ? 'Saving...' : 'Adding...') : (currentQuestion ? 'Save Changes' : 'Add Question')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuestionList;